/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useEffect, useRef } from "react";

import { gql, useLazyQuery, useQuery } from "@apollo/client";

import {
  FREELANCE_PROFILE_FRAGMENT,
  FreelanceProfileFragment,
} from "graphql/fragments";
import { QueryFreelanceProfilesArgs } from "graphql/types";

import { PaginatedPayload } from "./shared/types";

export const FREELANCE_PROFILES = gql`
  query (
    $page: Int
    $perPage: Int
    $sortBy: FreelanceProfilesSortByEnum
    $filter: FreelanceProfilesFilter
  ) {
    freelanceProfiles(
      page: $page
      perPage: $perPage
      sortBy: $sortBy
      filter: $filter
    ) {
      exportPath
      items {
        ...FreelanceProfileFragment
      }
      total
    }
  }
  ${FREELANCE_PROFILE_FRAGMENT}
`;

export type FreelanceProfilesQuery = {
  freelanceProfiles: PaginatedPayload<FreelanceProfileFragment>;
};

type QueryArgs = Pick<
  QueryFreelanceProfilesArgs,
  "page" | "perPage" | "sortBy" | "filter"
>;

export const useFreelanceProfilesQuery = (args: QueryArgs = {}) => {
  const { data, loading: isLoading } = useQuery<
    FreelanceProfilesQuery,
    QueryArgs
  >(FREELANCE_PROFILES, {
    variables: { ...args },
  });

  const freelanceProfiles =
    data?.freelanceProfiles.items?.filter(Boolean) ?? [];

  return {
    freelanceProfiles,
    isLoading,
  };
};

export const useFreelanceProfilesLazyQuery = () => {
  const [getFreelanceProfilesQuery, { data, loading: isLoading, called }] =
    useLazyQuery<FreelanceProfilesQuery, QueryArgs>(FREELANCE_PROFILES, {
      fetchPolicy: "network-only",
    });

  const freelanceProfiles =
    data?.freelanceProfiles.items?.filter(Boolean) ?? [];
  const total = data?.freelanceProfiles.total ?? 0;

  const resolveRef =
    useRef<(freelanceProfiles?: FreelanceProfileFragment[]) => void>(undefined);

  useEffect(() => {
    if (called && !isLoading && resolveRef.current) {
      resolveRef.current(freelanceProfiles);
      resolveRef.current = undefined;
    }
  }, [freelanceProfiles, called, isLoading]);

  const getFreelanceProfiles = async (args?: QueryArgs) => {
    const variables = { ...args };

    getFreelanceProfilesQuery({ variables });

    return new Promise<FreelanceProfileFragment[] | undefined>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  return { getFreelanceProfiles, freelanceProfiles, total, isLoading, called };
};
